import React, { useCallback, useEffect, useState } from 'react';
import SecondaryButton from '@carvana/showroom-forms/Button/Secondary';
import { typeStyle } from '@carvana/showroom-css-theme';
import { datadogRum } from '@datadog/browser-rum';
import { useIntersectionObserver } from 'usehooks-ts';
import {
  setCarvanaCareWarranty,
  setDeductibleState,
  setExperimentPaintedDoor,
  setPaintedDoorUserInfo,
  setSaleEffectiveDate,
} from '@store/sar-store';
import { getUserState } from '@carvana/core-auth';
import { LinkWithNativeEvents } from '../_common/link/link';
import { RouterProvider } from './providers/router';
import { UserSelectionsProvider } from './providers/user-selections';
import styles from './service-and-repair.module.css';
import { anyCarvanaCare, Warranty } from './constants';
import { Modal } from './components/modal';
import ServiceAndRepairIcon from '../../assets/service-and-repair-icon.svg';
import { ReportedConcern } from './components/reported-concern/reported-concern';
import { Analytics } from '../../services/analytics-service';
import { AnalyticEvents } from '../../constants';
import { RepairFacilitiesProvider } from './providers/repair-facilities';
import { clearSessionId } from './utilities';
import { runtimeConfig } from './services/base-url-config';

const UserSelectionModal = ({ modalIsOpen, setModalIsOpen, feedData }: any) => {
  const onCloseModal = () => {
    setModalIsOpen(false);
    Analytics.trackEvent(AnalyticEvents.sarDismissed);
    datadogRum.addAction(AnalyticEvents.sarDismissed);
    clearSessionId();
  };

  return modalIsOpen ? (
    <UserSelectionsProvider>
      <RepairFacilitiesProvider>
        <RouterProvider closeModal={onCloseModal}>
          <Modal closeModal={onCloseModal} feedData={feedData} className={styles['modal-style-overrides']} />
        </RouterProvider>
      </RepairFacilitiesProvider>
    </UserSelectionsProvider>
  ) : null;
};

export const ServiceAndRepairCard = ({
  feedData,
  positionValue,
  env,
  hideCard = false,
  registerGetStartedCallback,
}: any) => {
  const sar = feedData?.content?.serviceAndRepair;
  const vehicleId = sar?.vehicleId || sar?.vehicle?.vehicleId;

  const { ref } = useIntersectionObserver({
    freezeOnceVisible: true,
    onChange: (isIntersecting) => {
      if (isIntersecting) {
        Analytics.trackEvent(AnalyticEvents.sarViewed, { vehicleId });
        datadogRum.addAction(AnalyticEvents.sarViewed, { vehicleId });
      }
    },
  });
  const [modalIsOpen, setModalIsOpen] = useState(false);

  useEffect(() => {
    Analytics.trackEvent(AnalyticEvents.sarRendered, { vehicleId });
    datadogRum.addAction(AnalyticEvents.sarRendered, { vehicleId });
  }, [vehicleId]);

  useEffect(() => {
    if (env) runtimeConfig.initialize(env);
  }, [env]);

  useEffect(() => {
    // register registration state and sale effective date to sar-store
    const sar = feedData?.content?.serviceAndRepair;
    setDeductibleState(sar?.deductibleState);
    setSaleEffectiveDate(sar?.saleEffectiveDate);
    const hasCarvanaCareWarranty = sar?.coverage?.find((x) => x?.type?.includes?.('CarvanaCare'));
    if (hasCarvanaCareWarranty?.isExpired === false) {
      setCarvanaCareWarranty(hasCarvanaCareWarranty?.type);
    }

    // mfer data
    if (sar?.title && sar?.vehicle) {
      setPaintedDoorUserInfo(sar?.vehicle?.year, sar?.vehicle?.make, sar?.vehicle?.model, sar?.title);
    }

    // mfer data
    if (sar?.assignedBucket) {
      setExperimentPaintedDoor(sar?.assignedBucket);
    }
  }, [feedData]);

  const hasConcerns = feedData?.content?.serviceAndRepair?.reportedConcerns?.length > 0;
  const { coverage } = feedData?.content?.serviceAndRepair || {};
  const shouldShowModalButton = !!feedData?.content?.serviceAndRepair?.coverage;

  const shouldShowRoadside = coverage
    ?.filter((y: any) => !y.isExpired)
    ?.some((x: any) => x.type === Warranty.LimitedWarranty || anyCarvanaCare(x.type));

  const onGetStartedClick = useCallback(() => {
    setModalIsOpen(true);
    Analytics.trackEvent(AnalyticEvents.sarClickedGetStarted, {
      is_impersonating: getUserState()?.impersonation?.is_impersonating,
    });
    datadogRum.addAction(AnalyticEvents.sarClickedGetStarted);
  }, []);

  useEffect(() => {
    if (registerGetStartedCallback) {
      registerGetStartedCallback(onGetStartedClick);
    }
  }, [registerGetStartedCallback, onGetStartedClick]);

  const onPhoneClick = () => {
    Analytics.trackEvent(AnalyticEvents.sarClickRoadsidePhoneNumber);
    datadogRum.addAction(AnalyticEvents.sarClickRoadsidePhoneNumber);
  };

  return (
    <>
      <UserSelectionModal
        modalIsOpen={modalIsOpen}
        setModalIsOpen={setModalIsOpen}
        feedData={feedData?.content?.serviceAndRepair}
      />

      {!hideCard && (
        <div ref={ref} className={styles['service-and-repair-container']}>
          <div className={styles['service-and-repair-header']} id="service-and-repair-header">
            <img src={ServiceAndRepairIcon} alt="service-and-repair" />
            <div style={typeStyle('header-m')}>Service and Repair</div>
          </div>

          {shouldShowModalButton && (
            <>
              <div className={styles['service-and-repair-content']}>
                <div style={typeStyle('body-s')} id="service-and-repair-text">
                  Need to service your vehicle? Answer a few questions and we&apos;ll guide you to the right service
                  provider.
                </div>
                <SecondaryButton testId="get-started-btn" type="button" onClick={onGetStartedClick}>
                  GET STARTED
                </SecondaryButton>
              </div>

              {shouldShowRoadside && (
                <div className={styles['service-and-repair-footer']}>
                  <div style={typeStyle('header-xs')}>Need roadside assistance instead?</div>

                  <div style={typeStyle('body-s')}>
                    Call SilverRock at{' '}
                    <LinkWithNativeEvents
                      href="tel:1-877-584-3848"
                      onClick={onPhoneClick}
                      nativeEvent={{
                        event: 'link',
                        payload: {
                          url: 'tel:1-877-584-3848',
                        },
                      }}
                    >
                      1-877-584-3848
                    </LinkWithNativeEvents>{' '}
                    for towing, jump starts, flat tire assistance, locksmith services, and emergency gas/fluids.
                  </div>
                </div>
              )}
            </>
          )}

          {hasConcerns && <ReportedConcern reportedConcerns={feedData?.content?.serviceAndRepair?.reportedConcerns} />}
        </div>
      )}
    </>
  );
};

export default ServiceAndRepairCard;
